import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Presentation, Settings2, Share, Users } from 'lucide-react';
import { getLiveHostRead, LiveReadError } from '@api-client/liveReads';
import { LiveBadge } from '@components/live/LiveBadge';
import { pollShare } from '@components/polls/pollFormat';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@components/ui/drawer';
import { liveHostKey } from '@lib/cache/polls';
import { useLiveQueryPolling } from '@lib/livePolls/useLiveQueryPolling';
import type { LiveHostState } from '@models/api';
import { BigScreen } from './bigScreen/BigScreen';
import { HostControls, Progress } from './HostControls';
import { LIVE_HOST_COPY, LIVE_POLL_COPY } from './livePollCopy';
import { shareScreenLink } from './screenLinkActions';

/** True while the element is in the viewport (no observer, as in tests: always). */
const useOnScreen = (ref: React.RefObject<HTMLElement | null>): boolean => {
  const [onScreen, setOnScreen] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      setOnScreen(entries.some(entry => entry.isIntersecting));
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [ref]);
  return onScreen;
};

/** A tap on these must not reach the card (it opens the Post sheet). */
const stop = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

export interface AuthorLiveCardProps {
  /** The poll's `live_code` (the author's only). */
  code: string;
}

/**
 * L9, the author's card while the poll is live (TECH-867 § Views L9, L9b): the Live pill, "Question {n} of {m}"
 * with its progress, the bars and "{n} answers", a 16:9 thumbnail of the big screen (the real one scaled, L7,
 * L27) with "Open screen" and "Share screen link", and "Host controls" (ink, 44px) opening the same controls in
 * a drawer (`HostControls size="compact"`). Reads `/host` every 2 s while it is on screen. A tap on the card's
 * body still opens the Post sheet; only these buttons stay here.
 */
export const AuthorLiveCard: React.FC<AuthorLiveCardProps> = ({ code }) => {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const onScreen = useOnScreen(ref);
  const [open, setOpen] = useState(false);
  const [gone, setGone] = useState(false);

  const read = useCallback(
    async (signal: AbortSignal): Promise<LiveHostState> => {
      const answer = await getLiveHostRead(code, signal).catch((error: unknown) => {
        if (error instanceof LiveReadError && error.httpStatus === 403) setGone(true);
        throw error;
      });
      if (!answer.ok) {
        setGone(true);
        throw new LiveReadError(404);
      }
      return answer.data;
    },
    [code],
  );
  const { data: host } = useLiveQueryPolling<LiveHostState>({
    queryKey: liveHostKey(code),
    read,
    enabled: !gone && (onScreen || open),
  });

  const state = host?.state;
  const question = state?.question ?? null;
  const live = state?.live_state === 'live' && state.current_position !== null && question !== null;
  const position = state?.current_position ?? 0;
  const screenPath = host
    ? `/live/${encodeURIComponent(code)}/screen?key=${encodeURIComponent(host.presenter_key)}`
    : null;

  return (
    <div ref={ref} className="flex flex-col gap-3" data-testid="author-live-card">
      {state && live && question ? (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="flex shrink-0 items-center gap-2">
              <LiveBadge />
              <span className="text-sm leading-[18px] font-semibold whitespace-nowrap text-mb-ink">
                {LIVE_HOST_COPY.questionOf(position + 1, state.questions_count)}
              </span>
            </span>
            <Progress count={state.questions_count} upTo={position} />
          </div>
          <p className="text-base leading-5 font-semibold break-words text-mb-ink">
            {question.text}
          </p>
          <ul className="flex flex-col gap-1.5">
            {question.options.map(option => {
              const share = pollShare(option.votes_count, question.voters_count);
              return (
                <li
                  key={option.id}
                  className="relative flex min-h-9 items-center gap-2 overflow-hidden rounded-[10px] border border-mb-border bg-white px-3"
                >
                  <span
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 bg-mb-teal-light"
                    style={{ width: `${share}%` }}
                  />
                  <span className="relative min-w-0 flex-1 py-1.5 text-sm leading-[18px] break-words text-mb-ink">
                    {option.text}
                  </span>
                  <span className="relative shrink-0 text-[13px] leading-4 font-medium text-mb-gray-dark">
                    {LIVE_HOST_COPY.shareAndCount(share, option.votes_count)}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="flex items-center gap-1.5 text-xs leading-4 font-medium text-mb-dark-gray">
            <Users className="size-3.5" aria-hidden="true" />
            {LIVE_POLL_COPY.answers(question.voters_count)}
          </p>
        </>
      ) : null}

      {state && host ? (
        <div className="flex items-center gap-3 rounded-[14px] bg-mb-bg p-2.5">
          <div
            className="pointer-events-none aspect-video w-32 shrink-0 overflow-hidden rounded-lg border border-mb-border"
            aria-hidden="true"
            inert
            data-testid="author-screen-thumbnail"
          >
            <BigScreen state={state} />
          </div>
          <div className="flex min-w-0 flex-col">
            <button
              type="button"
              onClick={event => {
                stop(event);
                if (screenPath) navigate(screenPath);
              }}
              className="inline-flex min-h-11 items-center gap-1.5 text-sm leading-[18px] font-medium text-mb-teal-ink"
            >
              <Presentation className="size-4" strokeWidth={2} aria-hidden="true" />
              {LIVE_HOST_COPY.openScreen}
            </button>
            <button
              type="button"
              onClick={event => {
                stop(event);
                void shareScreenLink(code, host.presenter_key, state.title);
              }}
              className="inline-flex min-h-11 items-center gap-1.5 text-sm leading-[18px] font-medium text-mb-teal-ink"
            >
              <Share className="size-4" strokeWidth={2} aria-hidden="true" />
              {LIVE_HOST_COPY.shareScreenLink}
            </button>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        disabled={!host}
        onClick={event => {
          stop(event);
          setOpen(true);
        }}
        className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-mb-teal-ink text-[15px] leading-5 font-semibold text-white disabled:opacity-60"
      >
        <Settings2 className="size-4" strokeWidth={2} aria-hidden="true" />
        {LIVE_HOST_COPY.hostControls}
      </button>
      <p className="text-xs leading-4 text-mb-dark-gray">{LIVE_HOST_COPY.onlyYouSeeHostControls}</p>

      {/* The drawer portals out, but its taps still bubble through React: they stop here, not at the card. */}
      <div onClick={stop} onKeyDown={stop} role="presentation">
        <Drawer open={open && host !== undefined} onOpenChange={setOpen}>
          <DrawerContent className="border-x-0 border-b-0 pb-6" aria-describedby={undefined}>
            <DrawerHeader className="px-4 pt-1 pb-2 text-left">
              <DrawerTitle className="text-lg leading-6 font-semibold text-mb-ink">
                {LIVE_HOST_COPY.hostControls}
              </DrawerTitle>
            </DrawerHeader>
            <div className="px-4 pt-1 pb-8">
              {host ? <HostControls code={code} host={host} size="compact" /> : null}
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </div>
  );
};
